import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useLogoutApi } from '@/api/auth';
import ArrowRightIcon from '@/components/Icon/ArrowRightIcon';
import HomeIcon from '@/components/Icon/HomeIcon';
import SingleArrowNext from '@/components/Icon/SingleArrowNext';
import SingleArrowPrev from '@/components/Icon/SingleArrowPrev';
import AccountExit from '@/components/MyPage/AccountExit/AccountExit';
import BasicInfo from '@/components/MyPage/BasicInfo/BasicInfo';
import CompanyInfo from '@/components/MyPage/CompanyInfo/CompanyInfo';
import EducationInfo from '@/components/MyPage/EducationInfo/EducationInfo';
import EnrollmentCert from '@/components/MyPage/EnrollmentCert/EnrollmentCert';
import ModifyPassword from '@/components/MyPage/ModifyPassword/ModifyPassword';
import PrivacyTerms from '@/components/MyPage/PrivacyTerms/PrivacyTerms';
import ServiceTerms from '@/components/MyPage/ServiceTerms/ServiceTerms';
import {
	MYPAGE_MENU_ACCOUNT_EXIT,
	MYPAGE_MENU_BASIC,
	MYPAGE_MENU_COMPANY,
	MYPAGE_MENU_EDUCATION,
	MYPAGE_MENU_ENROLLMENT,
	MYPAGE_MENU_PASSWORD,
	MYPAGE_MENU_PRIVACY_TERMS,
	MYPAGE_MENU_SERVICE_TERMS,
	mypageMenuList,
	normalizeMypageMenu,
} from '@/constants/mypage';
import useIsMobile from '@/hooks/useIsMobile';
import { useAuthStore } from '@/store/authStore';
import { useTokenStore } from '@/store/tokenStore';

import * as S from './style';

const MyPage = () => {
	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();
	const { name, clearValues } = useAuthStore();
	const { resetToken } = useTokenStore();
	const { mutateAsync: logout } = useLogoutApi();
	const isMobile = useIsMobile(1180);

	const rawMenu = (location.state as { menu?: string } | null)?.menu;
	const isHubView = isMobile && rawMenu === undefined;
	const activeMenu = isHubView ? null : normalizeMypageMenu(rawMenu);

	useEffect(() => {
		if (!isMobile) return;
		document.body.classList.add('hidden');
		return () => document.body.classList.remove('hidden');
	}, [isMobile]);

	const releaseModal = () => {
		document.body.classList.remove('hidden');
	};

	const handleLogout = async () => {
		try {
			await logout();
			clearValues();
			resetToken();
			releaseModal();
			navigate('/');
		} catch (error) {
			console.error('Logout failed:', error);
		}
	};

	const handleMenuSelect = (menu: string) => {
		navigate('/mypage', { state: { menu } });
	};

	const handleBackToHub = () => {
		navigate('/mypage', { replace: true, state: null });
	};

	const handleGoHome = () => {
		releaseModal();
		navigate('/');
	};

	const renderContent = () => {
		if (!activeMenu) return null;

		switch (activeMenu) {
			case MYPAGE_MENU_BASIC:
				return <BasicInfo />;
			case MYPAGE_MENU_COMPANY:
				return <CompanyInfo />;
			case MYPAGE_MENU_EDUCATION:
				return <EducationInfo />;
			case MYPAGE_MENU_ENROLLMENT:
				return <EnrollmentCert />;
			case MYPAGE_MENU_PASSWORD:
				return <ModifyPassword />;
			case MYPAGE_MENU_SERVICE_TERMS:
				return <ServiceTerms />;
			case MYPAGE_MENU_PRIVACY_TERMS:
				return <PrivacyTerms />;
			case MYPAGE_MENU_ACCOUNT_EXIT:
				return <AccountExit />;
			default:
				return <BasicInfo />;
		}
	};

	const renderMobileHub = () => (
		<S.MobileHub>
			<S.MobileProfileCard>
				<S.MobileProfileName>
					{name}
					{t('mypage.page.userSuffix')}
				</S.MobileProfileName>
				<S.MobileLogoutButton type="button" onClick={handleLogout}>
					{t('common.actions.logout')}
					<ArrowRightIcon iconColor="#fff" width="16" height="16" />
				</S.MobileLogoutButton>
			</S.MobileProfileCard>

			<S.MobileMenuList>
				{mypageMenuList.map((item) => (
					<S.MobileMenuItem key={item.menu}>
						<S.MobileMenuButton type="button" onClick={() => handleMenuSelect(item.menu)}>
							<span>{t(item.menu)}</span>
							<SingleArrowNext width="20" height="20" iconColor="#868E96" />
						</S.MobileMenuButton>
					</S.MobileMenuItem>
				))}
			</S.MobileMenuList>
		</S.MobileHub>
	);

	if (isMobile) {
		return (
			<S.MobileModalOverlay role="dialog" aria-modal="true" aria-label={t('mypage.page.title')}>
				<S.MobileModalHeader>
					<S.ModalHeaderSide>
						{!isHubView ? (
							<S.ModalBackButton type="button" onClick={handleBackToHub}>
								<SingleArrowPrev width="18" height="18" iconColor="currentColor" />
								{t('common.actions.previous', { defaultValue: '이전' })}
							</S.ModalBackButton>
						) : null}
					</S.ModalHeaderSide>
					<S.MobileModalTitle>{isHubView ? t('mypage.page.title') : t(activeMenu ?? '')}</S.MobileModalTitle>
					<S.ModalHeaderSide $align="end">
						<S.ModalHomeButton
							type="button"
							onClick={handleGoHome}
							aria-label={t('common.actions.goHome', { defaultValue: '홈으로 이동' })}
						>
							<HomeIcon width="22" height="22" iconColor="currentColor" />
						</S.ModalHomeButton>
					</S.ModalHeaderSide>
				</S.MobileModalHeader>

				<S.MobileModalBody>{isHubView ? renderMobileHub() : <S.MobileDetailContent>{renderContent()}</S.MobileDetailContent>}</S.MobileModalBody>
			</S.MobileModalOverlay>
		);
	}

	return (
		<main>
			<S.Container>
				<S.Title>{t('mypage.page.title')}</S.Title>

				<S.Main>
					<S.Aside>
						<div className="info">
							<p className="title">
								{name} {t('mypage.page.userSuffix')}
							</p>
							<S.LogoutButton type="button" onClick={handleLogout}>
								{t('common.actions.logout')}
								<ArrowRightIcon iconColor="#fff" width="16" height="16" />
							</S.LogoutButton>
						</div>

						<S.MenuBoxList>
							{mypageMenuList.map((m) => (
								<S.MenuBox
									className={
										activeMenu === m.menu ||
										(activeMenu === MYPAGE_MENU_ACCOUNT_EXIT && m.menu === MYPAGE_MENU_BASIC)
											? 'active'
											: ''
									}
									key={m.menu}
									onClick={() => handleMenuSelect(m.menu)}
								>
									{t(m.menu)}
								</S.MenuBox>
							))}
						</S.MenuBoxList>
					</S.Aside>

					<S.MainContent>{renderContent() ?? <BasicInfo />}</S.MainContent>
				</S.Main>
			</S.Container>
		</main>
	);
};

export default MyPage;
